import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { api } from '../../services/api'
import Spinner from '../Spinner'
import type { CreatorVettingResponse } from '../../types'

/** GET /api/monetization/connect/status */
export interface ConnectStatus {
  status: 'not_started' | 'incomplete' | 'ready'
  stripe_configured?: boolean
  onboarding_url?: string | null
  message?: string
}

interface GetPaidChecklistProps {
  /** GET /api/compliance/creator-vetting/me; null when it couldn't be loaded. */
  vetting: CreatorVettingResponse | null
  onApply: () => void
  connect: ConnectStatus | null
  tierCount: number
  liveTierCount: number
  subscriberCount: number
  pageUrl: string | null
  onCreateTier: () => void
}

interface StepProps {
  done: boolean
  title: string
  detail: string
  children?: React.ReactNode
}

const Step: React.FC<StepProps> = ({ done, title, detail, children }) => (
  <li className="flex items-start gap-3 py-3">
    {done ? (
      <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" aria-label="Done" />
    ) : (
      <span
        className="h-6 w-6 flex-shrink-0 rounded-full border-2 border-gray-300"
        aria-label="Not done"
      />
    )}
    <div className="min-w-0 flex-1">
      <p className={`text-sm font-medium ${done ? 'text-gray-600' : 'text-gray-900'}`}>{title}</p>
      <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
      {children && <div className="mt-2">{children}</div>}
    </div>
  </li>
)

const VETTING_DETAIL = (vetting: CreatorVettingResponse | null): string => {
  switch (vetting?.status) {
    case 'approved':
      return 'Approved: you can charge for content.'
    case 'pending':
      return 'Pending: your application is under review. Check back here; we’ll update this page.'
    case 'rejected':
      return 'Not approved. Read the notes below, then resubmit.'
    case 'not_submitted':
      return 'Not submitted: a short application before you can charge for content.'
    default:
      return 'Couldn’t load your application status. Refresh to try again.'
  }
}

const button =
  'px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2'

/**
 * "Get paid" in four steps, each ticked from real data: approved to
 * monetize (creator vetting), Stripe Connect ready (connect status), a tier
 * live on Stripe (its price IDs), and the page shared (a first subscriber).
 */
const GetPaidChecklist: React.FC<GetPaidChecklistProps> = ({
  vetting,
  onApply,
  connect,
  tierCount,
  liveTierCount,
  subscriberCount,
  pageUrl,
  onCreateTier,
}) => {
  const [isStarting, setIsStarting] = useState(false)
  const approved = vetting?.status === 'approved'
  const connected = connect?.status === 'ready'
  const stripeMissing = connect?.stripe_configured === false
  const hasLiveTier = liveTierCount > 0
  const shared = subscriberCount > 0
  const doneCount = [approved, connected, hasLiveTier, shared].filter(Boolean).length

  if (doneCount === 4) return null

  const startOnboarding = async () => {
    setIsStarting(true)
    try {
      const { data } = await api.post<ConnectStatus>('/api/monetization/connect/onboarding')
      if (data.onboarding_url) {
        window.location.href = data.onboarding_url
      } else {
        toast.success('Stripe payouts are ready')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Couldn't start Stripe onboarding")
    } finally {
      setIsStarting(false)
    }
  }

  const copyLink = async () => {
    if (!pageUrl) return
    try {
      await navigator.clipboard.writeText(pageUrl)
      toast.success('Link copied')
    } catch {
      toast.error('Could not copy link')
    }
  }

  const connectDetail = stripeMissing
    ? "Payments aren't set up yet."
    : connected
    ? 'Payouts are on: Stripe sends your earnings to your bank.'
    : connect?.status === 'incomplete'
    ? 'Stripe still needs a few details before it can pay you.'
    : 'Stripe pays your earnings out to your bank. Takes about 5 minutes.'

  return (
    <section className="bg-white rounded-lg shadow p-4 sm:p-6 mb-6" aria-labelledby="get-paid-title">
      <div className="flex items-baseline justify-between gap-2">
        <h2 id="get-paid-title" className="text-lg font-bold text-gray-900">
          Get paid
        </h2>
        <span className="text-xs text-gray-500">{doneCount} of 4 done</span>
      </div>
      <ol className="divide-y divide-gray-200 mt-2">
        <Step done={approved} title="Get approved to monetize" detail={VETTING_DETAIL(vetting)}>
          {vetting?.status === 'rejected' && vetting.review_notes && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-2">
              Reviewer&apos;s notes: {vetting.review_notes}
            </p>
          )}
          {(vetting?.status === 'not_submitted' || vetting?.status === 'rejected') && (
            <button type="button" onClick={onApply} className={button}>
              {vetting.status === 'rejected' ? 'Resubmit application' : 'Apply to monetize'}
            </button>
          )}
        </Step>
        <Step done={connected} title="Connect Stripe" detail={connectDetail}>
          {!connected && !stripeMissing && (
            <button type="button" onClick={startOnboarding} disabled={isStarting} className={button}>
              {isStarting && <Spinner size="sm" className="border-white border-t-transparent" />}
              {connect?.status === 'incomplete' ? 'Finish Stripe setup' : 'Connect Stripe'}
            </button>
          )}
        </Step>
        <Step
          done={hasLiveTier}
          title="Create a tier"
          detail={
            hasLiveTier
              ? `${liveTierCount} tier${liveTierCount === 1 ? '' : 's'} live on Stripe.`
              : tierCount > 0
              ? 'Your tier isn’t on Stripe yet: open it and save to put it there.'
              : 'A plan fans can subscribe to, e.g. “Pro — $15/month”.'
          }
        >
          {!hasLiveTier && tierCount === 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onCreateTier}
                disabled={vetting !== null && !approved}
                className={button}
              >
                Create a tier
              </button>
              {vetting !== null && !approved && (
                <span className="text-xs text-gray-500">Opens once you&apos;re approved.</span>
              )}
            </div>
          )}
        </Step>
        <Step
          done={shared}
          title="Share your page"
          detail={
            shared
              ? 'Your first subscriber has joined.'
              : 'Ticked when your first subscriber joins.'
          }
        >
          {!shared && pageUrl && (
            <div className="flex flex-wrap items-center gap-2">
              <code className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-1 break-all">
                {pageUrl}
              </code>
              <button
                type="button"
                onClick={copyLink}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Copy link
              </button>
            </div>
          )}
        </Step>
      </ol>
    </section>
  )
}

export default GetPaidChecklist
